import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  Flex,
  Form,
  Row,
  Switch,
  Tooltip,
  TooltipProps,
  Typography,
} from 'antd';
import React, { CSSProperties, ReactElement, ReactNode } from 'react';
import {
  Control,
  Controller as ControllerHookForm,
  ControllerRenderProps,
} from 'react-hook-form';

import { toFieldStatus } from 'src/app/utils/toFieldStatus';

import * as S from './SwitchController.style';
const { Item: FormItem } = Form;

interface SwitchControllerProps {
  control: Control<any>;
  name: string;
  placeholder?: string;
  defaultValue?: boolean;
  autoComplete?: string;
  onChange?: (
    checked: boolean,
    field: ControllerRenderProps<any, string>
  ) => void;
  prefix?: React.ReactNode;
  type?: string;
  label?: string;
  sideLabel?: boolean;
  rightLabel?: string;
  leftLabel?: string;
  required?: boolean;
  description?: string;
  tooltip?: ReactNode | (TooltipProps & { icon?: ReactElement });
  disabled?: boolean;
  horizontal?: boolean;
  style?: CSSProperties;
}

export const SwitchController: React.FC<SwitchControllerProps> = ({
  control,
  defaultValue,
  name,
  label,
  sideLabel,
  rightLabel,
  leftLabel,
  required,
  description,
  tooltip,
  disabled,
  horizontal,
  style,
  onChange,
}) => {
  const sideLabelSwitch = (field: ControllerRenderProps<any, string>) => {
    const { value, onChange: fieldOnChange, ...fieldProps } = field;

    return (
      <S.SwitchContainer>
        {leftLabel && (
          <S.LeftLabel
            style={{
              color: field.value ? '#434343' : '#ACACAC',
            }}
          >
            {leftLabel}
          </S.LeftLabel>
        )}
        <Switch
          aria-label={leftLabel || rightLabel ? leftLabel || rightLabel : label}
          checked={value}
          disabled={disabled}
          {...fieldProps}
          onChange={checked => {
            if (onChange) {
              onChange(checked, field);
              return;
            }

            fieldOnChange(checked);
          }}
        />
        {rightLabel && (
          <S.RightLabel
            style={{
              color: value ? '#ACACAC' : '#434343',
            }}
          >
            {rightLabel}
          </S.RightLabel>
        )}
      </S.SwitchContainer>
    );
  };

  return (
    <ControllerHookForm
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => {
        const { value, onChange: fieldOnChange, ...fieldProps } = field;
        const { status, help } = toFieldStatus(fieldState.error);
        return (
          <FormItem
            validateStatus={status}
            help={help}
            label={sideLabel || horizontal ? null : label}
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              marginTop: '10px',
              ...style,
            }}
            {...(required && { required: required })}
            {...(tooltip && !horizontal && { tooltip: tooltip })}
          >
            {sideLabel ? (
              sideLabelSwitch(field)
            ) : (
              <Row
                style={{
                  flexDirection: 'row',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                {horizontal ? (
                  <Flex style={{ alignItems: 'center', gap: '8px' }}>
                    <Typography>{label}</Typography>
                    {!!tooltip && (
                      <Tooltip title={String(tooltip)}>
                        <QuestionCircleOutlined />
                      </Tooltip>
                    )}
                  </Flex>
                ) : null}
                <Switch
                  aria-label={label}
                  checked={value}
                  {...fieldProps}
                  onChange={checked => {
                    if (onChange) {
                      onChange(checked, field);
                      return;
                    }

                    fieldOnChange(checked);
                  }}
                  disabled={disabled}
                />
                {description && <Typography>{description}</Typography>}
              </Row>
            )}
          </FormItem>
        );
      }}
    />
  );
};
